import { atom } from 'jotai';

import { showToastAtom } from 'src/stores/toast/atoms';
import { UTMParamsAtom } from 'src/stores/utm/atoms';
import {
  BillingInfoRequest,
  BillingInfoResponse,
  getErrorCode,
  PaymentPopupInfo,
  PaymentsReservation,
  PgPaymentMethodType,
  PurchasableProduct,
} from 'src/types/Payments';
import { ToastType } from 'src/types/Toast';
import { client } from 'src/utils/api';
import { isAxiosError, parseError } from 'src/utils/error';
import { getAtomWithSessionStorage } from 'src/utils/localStorageUtils';

import { deletePaymentMethodAPI, reservePaymentAPI } from './apis';
import { GetPurchasableSubscriptionProducts, DeletePaymentMethodRequest } from './types';

export const PAYMENT_POPUP_INFO_KEY = 'paymentInfo';
export const paymentPopupInfoAtom = getAtomWithSessionStorage<PaymentPopupInfo | null>(
  PAYMENT_POPUP_INFO_KEY,
  null
);
export const productToPurchaseAtom = atom<PurchasableProduct | null>(null);

export const paymentsErrorAtom = atom<{
  errorCode: string;
} | null>(null);

export const billingInfoAtom = atom<BillingInfoResponse | null>(null);

export const getBillingInfoAtom = atom(
  null,
  async (_get, set, { productId, ...params }: BillingInfoRequest) => {
    const { data: billingInfo } = await client.get<BillingInfoResponse>(
      `/api/payments/billingInfo/${productId}`,
      { params }
    );
    set(billingInfoAtom, billingInfo);
  }
);

export const paymentsReservationAtom = atom<PaymentsReservation | null>(null);

export const reservePaymentAtom = atom(
  null,
  async (get, set, paymentMethod: PgPaymentMethodType) => {
    const paymentPopupInfo = get(paymentPopupInfoAtom);
    const product = paymentPopupInfo?.product;
    if (!paymentMethod || !product) return;

    reservePaymentAPI(product, paymentMethod, get(UTMParamsAtom))
      .then((reservation) => {
        set(paymentsReservationAtom, reservation);
      })
      .catch((e) => {
        const errorCode = getErrorCode(e.response?.data?.code);
        set(paymentsErrorAtom, { errorCode });
      });
  }
);

export const purchasableSubscriptionProductsAtom = atom<
  GetPurchasableSubscriptionProducts['subscriptionGroups'] | void
>(undefined);
export const getPurchasableSubscriptionProductsAtom = atom(null, async (get, set) => {
  const { data } = await client.get<GetPurchasableSubscriptionProducts>(
    '/api/v1/subscriptions/purchasable-products'
  );
  set(purchasableSubscriptionProductsAtom, data.subscriptionGroups);
});

export const deletePaymentMethodAtom = atom(
  null,
  (get, set, params: DeletePaymentMethodRequest) => {
    return deletePaymentMethodAPI(params).catch((e) => {
      if (!isAxiosError(e)) throw e;
      if (e.response?.status === 500) {
        set(showToastAtom, {
          message: parseError(e as Error),
          type: ToastType.ERROR,
        });
      }
      throw e;
    });
  }
);

export const isPurchasedStarterPackageAtom = atom(false);
